if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    static componentScripts = new Map();

    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      this.modalContent.addEventListener('click', event => {
        if (!event.target.matches('input[type="radio"][name="Color"]')) return;
        this.modalContent.querySelectorAll('input[type="radio"][name="Color"]').forEach(input => {
          const swatch = input.nextElementSibling;
          if (swatch?.matches('.swatch-element')) swatch.classList.toggle('clicked', input === event.target);
        });
      });
    }

    hide(preventFocus = false) {
      this.loadController?.abort();
      this.loadRevision = (this.loadRevision || 0) + 1;
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      if (opener.getAttribute('aria-disabled') === 'true') return;
      this.loadController?.abort();
      const revision = this.loadRevision = (this.loadRevision || 0) + 1;
      const controller = this.loadController = new AbortController();
      opener.parentElement.querySelector('[data-nova-quick-add-error]')?.remove();
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');
      return fetch(opener.getAttribute('data-product-url'), { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(window.cartStrings.error);
          return response.text();
        })
        .then(async (responseText) => {
          if (revision !== this.loadRevision || controller.signal.aborted) return;
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          if (!this.productElement) throw new Error(window.cartStrings.error);
          this.preventDuplicatedIDs();        
          this.removeDOMElements();
          await this.setInnerHTML(this.modalContent, this.productElement.innerHTML);
          if (revision !== this.loadRevision || controller.signal.aborted) return;
          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();         
          this.preventVariantURLSwitching();
          super.show(opener);
        })
        .catch((error) => {
          if (controller.signal.aborted || error.name === 'AbortError') return;
          // Leave a visible, accessible error next to the original quick-add
          // button; ordinary product links remain usable for recovery.
          let status = opener.parentElement.querySelector('[data-nova-quick-add-error]');
          if (!status) {
            status = document.createElement('p');
            status.className = 'product-form__error-message';
            status.setAttribute('data-nova-quick-add-error', '');
            status.setAttribute('role', 'alert');
            opener.parentElement.appendChild(status);
          }
          status.textContent = window.cartStrings.error;
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');          
        });
    }

    async setInnerHTML(element, html) {
      element.innerHTML = html;

      const componentNames = {
        'product-form.js': 'product-form',
        'pickup-availability.js': 'pickup-availability',
        'product-modal.js': 'product-modal',
        'media-gallery.js': 'media-gallery',
        'share.js': 'share-button'
      };
      const pending = [];
      element.querySelectorAll('script').forEach(oldScriptTag => {
        if (oldScriptTag.src) {
          const url = new URL(oldScriptTag.src, document.baseURI);
          const component = componentNames[url.pathname.split('/').pop()];
          const themeAsset = (url.origin === location.origin && /\/cdn\/shop\/t\/\d+\/assets\//.test(url.pathname)) ||
            (url.hostname === 'cdn.shopify.com' && /\/s\/files\/.+\/t\/\d+\/assets\//.test(url.pathname));
          if (component && themeAsset) {
            oldScriptTag.remove();
            if (element.querySelector(component) && !customElements.get(component)) {
              pending.push(this.loadComponentScript(oldScriptTag, component));
            }
            return;
          }
        } else if (oldScriptTag.type === 'application/json' || oldScriptTag.type === 'application/ld+json') {
          return;
        }
        // Preserve inline initialization and app/payment scripts as supplied.
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
      await Promise.all(pending);
    }

    loadComponentScript(source, component) {
      const loads = this.constructor.componentScripts;
      if (loads.has(source.src)) return loads.get(source.src);
      const script = document.createElement('script');
      Array.from(source.attributes).forEach(attribute => script.setAttribute(attribute.name, attribute.value));
      script.async = false;
      const promise = new Promise((resolve, reject) => {
        const fail = () => {
          loads.delete(source.src);
          script.remove();
          reject(new Error(window.cartStrings.error));
        };
        script.addEventListener('load', () => customElements.get(component) ? resolve() : fail(), { once: true });
        script.addEventListener('error', fail, { once: true });
      });
      loads.set(source.src, promise);
      // Keep pending definitions alive if their first dialog closes while loading.
      document.head.appendChild(script);
      return promise;
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (variantPicker) variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();      
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
        variantSelect.dataset.originalSection = sectionId;
      });      
          
    }

    removeGalleryListSemantic() {      
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');      
      if (!galleryList) return;     
      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
      

      const FBT = this.modalContent.querySelector('[id^="dT_bundleSelector"]');        
      if (FBT) FBT.remove();
      const Tabs = this.modalContent.querySelector('[class^="product__info-as-bottom-tabs"]');
      if (Tabs) Tabs.remove();
      
    }

  });
}
